import { memo } from "react";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  FaBuilding,
  FaIndustry,
  FaGlobe,
  FaInfoCircle,
  FaLink,
  FaBullseye,
  FaEnvelope,
  FaPhone,
  FaLanguage,
  FaUser,
  FaDollarSign,
  FaChartLine,
  FaMapPin,
  FaMapMarkedAlt,
  FaBoxes,
  FaBoxOpen,
  FaTag,
  FaPercentage,
  FaIdBadge,
  FaUserCheck,
  FaTicketAlt,
} from "react-icons/fa";
import { User } from "../../types/Types";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { isPaidPortalMember } from "../../utils/membershipTier";

interface BusinessProfileProps {
  userData: User;
}

const IMG_OBJECT_FIT_STYLE = { objectFit: "contain" as const };

const BusinessProfile = memo(function BusinessProfile({
  userData,
}: BusinessProfileProps) {
  const { t, i18n } = useTranslation();
  const np = () => t("account.notProvided");
  const dynamicFields = {
    businessName: userData.businessName,
    businessSectorIndustry: userData.businessSectorIndustry,
    businessCountryOperation: userData.businessCountryOperation,
    businessAbout: userData.businessAbout,
    businessWebsiteUrl: userData.businessWebsiteUrl,
    businessObjectives: userData.businessObjectives,
    personalAbout: userData.personalAbout,
    email: userData.email,
    businessTelephoneNumber: userData.businessTelephoneNumber,
    personalLanguagePreference: userData.personalLanguagePreference,
    personalGender: userData.personalGender,
    businessCurrency: userData.businessCurrency,
    businessTurnover: userData.businessTurnover,
    county: userData.county,
    businessPostCode: userData.businessPostCode,
    businessAddressLineOne: userData.businessAddressLineOne,
    businessAddressLineTwo: userData.businessAddressLineTwo,
    businessProductServices: userData.businessProductServices,
    productInterested: userData.productInterested,
    businessDiscountCode: userData.businessDiscountCode,
    businessDiscount: userData.businessDiscount,
    membershipName: userData.membershipName,
    referralCode: userData.referralCode,
    referralCodeUsed: userData.referralCodeUsed,
  };
  const { data: translated } = useTranslateDynamicFields(
    dynamicFields,
    i18n.resolvedLanguage || i18n.language
  );
  const tx = (key: keyof typeof dynamicFields, fallback?: string | null) =>
    translated?.[key] || fallback || np();
  const showMembershipFields = isPaidPortalMember(userData);

  return (
    <div className="account-details-stable-order">
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">{t("account.section.businessDetails")}</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col md={4} className="mb-3 d-block d-md-none">
            <div className="d-flex align-items-start">
              <FaBuilding size={20} className="me-2 mt-1 text-secondary" />
              <div className="d-flex justify-content-between w-100">
                <div>
                  <strong>{t("account.field.businessName")}</strong>
                  <div className="text-muted small">
                    {tx("businessName", userData.businessName)}
                  </div>
                </div>
                <img
                  src={userData.BusinessLogo}
                  className="rounded-circle me-2 mt-1"
                  width={40}
                  height={40}
                  style={IMG_OBJECT_FIT_STYLE}
                  alt={t("account.alt.brandLogo")}
                />
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaIndustry size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.sectorIndustry")}</strong>
                <div className="text-muted small">
                  {tx("businessSectorIndustry", userData.businessSectorIndustry)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaGlobe size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.countriesOfOperation")}</strong>
                <div className="text-muted small">
                  {tx("businessCountryOperation", userData.businessCountryOperation)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaInfoCircle size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.aboutBusiness")}</strong>
                <div className="text-muted small">
                  {tx("businessAbout", userData.businessAbout)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaLink
                size={20}
                className="me-2 mt-1 text-secondary flex-shrink-0"
              />
              <div className="d-flex flex-column w-100">
                <strong>{t("account.field.websiteUrl")}</strong>
                <a
                  href={userData.businessWebsiteUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted small text-truncate pr-1"
                >
                  {tx("businessWebsiteUrl", userData.businessWebsiteUrl)}
                </a>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaBullseye size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.objectives")}</strong>
                <div className="text-muted small">
                  {tx("businessObjectives", userData.businessObjectives)}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </div>

      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">{t("account.section.about")}</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col className="mb-3">
            <div className="text-muted small">
              {tx("personalAbout", userData.personalAbout)}
            </div>
          </Col>
        </Row>
        </div>
      </div>

      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">{t("account.section.personalDetails")}</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaEnvelope size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.email")}</strong>
                <div className="text-muted small">
                  {tx("email", userData.email)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaPhone size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.contactNumber")}</strong>
                <div className="text-muted small">
                  {tx("businessTelephoneNumber", userData.businessTelephoneNumber)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaLanguage size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.languagePreference")}</strong>
                <div className="text-muted small">
                  {tx("personalLanguagePreference", userData.personalLanguagePreference)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaUser size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.gender")}</strong>
                <div className="text-muted small">
                  {tx("personalGender", userData.personalGender)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaDollarSign size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.currency")}</strong>
                <div className="text-muted small">
                  {tx("businessCurrency", userData.businessCurrency)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaChartLine size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.turnover")}</strong>
                <div className="text-muted small">
                  {tx("businessTurnover", userData.businessTurnover)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaMapPin size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.county")}</strong>
                <div className="text-muted small">
                  {tx("county", userData.county)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaMapPin size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.postalCode")}</strong>
                <div className="text-muted small">
                  {tx("businessPostCode", userData.businessPostCode)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaMapMarkedAlt size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.addressLineOne")}</strong>
                <div className="text-muted small">
                  {tx("businessAddressLineOne", userData.businessAddressLineOne)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaMapMarkedAlt size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.addressLineTwo")}</strong>
                <div className="text-muted small">
                  {tx("businessAddressLineTwo", userData.businessAddressLineTwo)}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </div>

      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">{t("account.section.offerings")}</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaBoxes size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.productServices")}</strong>
                <div className="text-muted small">
                  {tx("businessProductServices", userData.businessProductServices)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaBoxOpen size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.productInterested")}</strong>
                <div className="text-muted small">
                  {tx("productInterested", userData.productInterested)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaTag size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.discountCode")}</strong>
                <div className="text-muted small">
                  {tx("businessDiscountCode", userData.businessDiscountCode)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaPercentage size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.discount")}</strong>
                <div className="text-muted small">
                  {tx("businessDiscount", userData.businessDiscount)}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </div>
      {/* Membership Details */}
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">{t("account.section.membershipDetails")}</h5>
        </div>
        <div className="p-3">
        <Row>
          {showMembershipFields && (
            <>
              <Col md={4} className="mb-3">
                <div className="d-flex align-items-start">
                  <FaIdBadge size={20} className="me-2 mt-1 text-secondary" />
                  <div>
                    <strong>{t("account.field.membershipName")}</strong>
                    <div className="text-muted small">
                      {tx("membershipName", userData.membershipName)}
                    </div>
                  </div>
                </div>
              </Col>
              <Col md={4} className="mb-3">
                <div className="d-flex align-items-start">
                  <FaUserCheck size={20} className="me-2 mt-1 text-secondary" />
                  <div>
                    <strong>{t("account.field.membershipType")}</strong>
                    <div className="text-muted small">
                      {userData.isAnnualMembership
                        ? t("account.billingCycle.annual")
                        : t("account.billingCycle.monthly")}
                    </div>
                  </div>
                </div>
              </Col>
            </>
          )}
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaTicketAlt size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.referralCode")}</strong>
                <div className="text-muted small">
                  {tx("referralCode", userData.referralCode)}
                </div>
              </div>
            </div>
          </Col>
          {userData.referralCodeUsed && (
            <Col md={4} className="mb-3">
              <div className="d-flex align-items-start">
                <FaTicketAlt size={20} className="me-2 mt-1 text-secondary" />
                <div>
                  <strong>{t("account.field.referralCodeUsed")}</strong>
                  <div className="text-muted small">
                    {tx("referralCodeUsed", userData.referralCodeUsed)}
                  </div>
                </div>
              </div>
            </Col>
          )}
        </Row>
        </div>
      </div>
    </div>
  );
});

BusinessProfile.displayName = "BusinessProfile";

export default BusinessProfile;
